/**
 * ZiroChat Interfaces Service
 * Wrapper for production chat.js global functions
 * This bridges the React app with the vanilla JS production chat code
 * 
 * @module ziroChatInterfaces
 */

/**
 * @typedef {Object} UserInfo
 * @property {string} email - User email address
 * @property {string} userId - User ID
 * @property {string} firstName - User first name
 * @property {string} cohortId - User cohort ID
 * @property {boolean} isAuthorized - Whether user is authorized
 */

/**
 * Check if production chat.js is loaded and available
 * @returns {boolean} True if chat.js functions are available
 */
export const isChatJSLoaded = () => {
  return typeof window.getBotResponse === 'function' &&
         typeof window.zt_initializeOpenAiThread === 'function';
};

/**
 * Send message to AI using production getBotResponse function
 * This function handles the full AI interaction including:
 * - Thread management
 * - Assistant selection
 * - Code context
 * - Response generation
 * 
 * @param {string} message - User message to send to AI
 * @returns {Promise<string>} AI-generated response text
 * @throws {Error} If production chat.js is not loaded
 * 
 * @example
 * const response = await ziroChatInterfaces.sendMessage("How do I create a button?");
 * console.log(response);
 */
export const sendMessage = async (message) => {
  if (!isChatJSLoaded()) {
    throw new Error('Production chat.js not loaded');
  }
  return await window.getBotResponse(message);
};

/**
 * Initialize OpenAI thread for conversation tracking
 * Creates a new thread or retrieves existing one for the current project
 * 
 * @param {boolean} [createNewThread=false] - Force creation of a new thread
 * @returns {Promise<void>}
 * @throws {Error} If production chat.js is not loaded
 * 
 * @example
 * await ziroChatInterfaces.initializeThread();
 * const threadId = ziroChatInterfaces.getThreadId();
 */
export const initializeThread = async (createNewThread = false) => {
  if (!isChatJSLoaded()) {
    throw new Error('Production chat.js not loaded');
  }
  if (window.zt_initializeOpenAiThread) {
    return await window.zt_initializeOpenAiThread(createNewThread);
  }
};

/**
 * Get the current OpenAI thread ID
 * 
 * @returns {string|null} Thread ID or null if not initialized
 * 
 * @example
 * const threadId = ziroChatInterfaces.getThreadId();
 * if (threadId) {
 *   console.log('Active thread:', threadId);
 * }
 */
export const getThreadId = () => {
  return window.OPENAI_THREAD_ID || null;
};

/**
 * Get the last AI response message content
 * 
 * @returns {string} Last AI message or empty string if none
 * 
 * @example
 * const lastMessage = ziroChatInterfaces.getLastResponse();
 */
export const getLastResponse = () => {
  return window.OPENAI_LAST_MESSAGE_CONTENT || '';
};

/**
 * Store current thread ID to backend for persistence
 * Saves conversation thread data including message history
 * 
 * @returns {Promise<Object>} Backend response with thread storage status
 * @throws {Error} If production function is not available
 * 
 * @example
 * await ziroChatInterfaces.storeThreadId();
 */
export const storeThreadId = async () => {
  if (typeof window.storeThreadId === 'function') {
    return await window.storeThreadId();
  }
  throw new Error('storeThreadId function not available');
};

/**
 * Store user feedback (like/dislike) for an AI response
 * Used for tracking response quality and user satisfaction
 * 
 * @param {string} messageId - OpenAI message ID to provide feedback for
 * @param {boolean} [liked=true] - True if user liked the response, false otherwise
 * @returns {Promise<Object>} Backend response with feedback status
 * @throws {Error} If production function is not available
 * 
 * @example
 * await ziroChatInterfaces.storeResponseFeedback('msg_123abc', true);
 */
export const storeResponseFeedback = async (messageId, liked = true) => {
  if (typeof window.storeResponseFeedback === 'function') {
    return await window.storeResponseFeedback(messageId, liked);
  }
  throw new Error('storeResponseFeedback function not available');
};

/**
 * Fetch user profile and check authorization status
 * Validates user access rights and updates global auth state
 * 
 * @returns {Promise<void>}
 * @throws {Error} If production function is not available
 * 
 * @example
 * await ziroChatInterfaces.fetchProfileAndCheckAuthorization();
 * const isAuthorized = ziroChatInterfaces.isUserAuthorized();
 */
export const fetchProfileAndCheckAuthorization = async () => {
  if (typeof window.fetchProfileAndCheckAuthorization === 'function') {
    return await window.fetchProfileAndCheckAuthorization();
  }
  throw new Error('fetchProfileAndCheckAuthorization function not available');
};

/**
 * Check if current user is authorized to use the service
 * 
 * @returns {boolean} True if user is authorized, false otherwise
 * 
 * @example
 * if (!ziroChatInterfaces.isUserAuthorized()) {
 *   console.log('User needs to upgrade subscription');
 * }
 */
export const isUserAuthorized = () => {
  return window.ZIRO_LOGGED_IN_USER_IS_AUTHORIZED === true;
};

/**
 * Get currently logged-in user information
 * Returns user details from global production state
 * 
 * @returns {UserInfo} User information object
 * 
 * @example
 * const user = ziroChatInterfaces.getLoggedInUserInfo();
 * console.log(`Welcome ${user.firstName}!`);
 */
export const getLoggedInUserInfo = () => {
  return {
    email: window.ZIRO_LOGGED_IN_USER_EMAIL || '',
    userId: window.ZIRO_LOGGED_IN_USER_ID || '',
    firstName: window.ZIRO_LOGGED_IN_USER_FIRST_NAME || '',
    cohortId: window.ZIRO_LOGGED_IN_USER_COHORT_ID || '',
    isAuthorized: window.ZIRO_LOGGED_IN_USER_IS_AUTHORIZED || false
  };
};

/**
 * Get helper function for cookies (used by production code)
 * @param {string} cname - Cookie name
 * @returns {string} Cookie value or empty string
 */
const getCookie = (cname) => {
  if (typeof window.getCookie === 'function') {
    return window.getCookie(cname);
  }
  // Fallback implementation
  const name = cname + "=";
  const decodedCookie = decodeURIComponent(document.cookie);
  const ca = decodedCookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return "";
};

/**
 * Get backend URL from production config
 * @returns {string} Backend URL
 */
const getBackendUrl = () => {
  // Try centralized config helpers first
  if (typeof window.getBackendUrl === 'function') {
    return window.getBackendUrl();
  }
  if (window.ZIRO_BACKEND_URL) {
    return window.ZIRO_BACKEND_URL;
  }
  // Last resort fallback (should not be needed if config_loader.js works)
  console.error('❌ ziroChatInterfaces.js: Backend URL not available from centralized config');
  return 'https://zirostudio.appspot.com';
};

/**
 * Fetch project metadata using production API pattern
 * Uses same endpoint and format as production ziro_studio.html
 * 
 * @param {string} projectCode - Project code/ID
 * @returns {Promise<Object>} Project data (array format matching production)
 * @throws {Error} If fetch fails
 * 
 * @example
 * const projectData = await ziroChatInterfaces.getProject('56dCAJWFE9vFbIs');
 * const project = projectData[0]; // Production returns array
 */
export const getProject = async (projectCode) => {
  if (!projectCode) {
    throw new Error('Project code is required');
  }
  
  const backendUrl = getBackendUrl();
  // Get session_id from cookie or localStorage (optional - fetchproject is public)
  let sessionId = getCookie('ziroatoken');
  if (!sessionId) {
    sessionId = localStorage.getItem('ziro_session_id');
  }
  // Only include session_id if it's a valid UUID (not legacy JWT)
  const isSessionId = sessionId && sessionId.includes('-') && sessionId.length === 36 && !sessionId.includes('.');
  const apiEndpoint = `${backendUrl}/fetchproject?file_id=${projectCode}${isSessionId ? `&session_id=${sessionId}` : ''}`;
  
  console.log('📂 Fetching project:', apiEndpoint);
  
  try {
    const response = await fetch(apiEndpoint, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    
    const data = await response.json();
    
    // Production returns array, ensure we return array format
    if (Array.isArray(data)) {
      return {
        success: true,
        data: data
      };
    } else if (data && typeof data === 'object') {
      // Handle single object response by wrapping in array
      return {
        success: true,
        data: [data]
      };
    }
    
    return {
      success: true,
      data: []
    };
  } catch (error) {
    console.error('❌ Error fetching project:', error);
    return {
      success: false,
      error: error.message,
      data: []
    };
  }
};

/**
 * Fetch project code using production API pattern
 * Uses same endpoint and format as production ziro_studio.html
 * 
 * @param {string} projectCode - Project code/ID
 * @returns {Promise<Object>} Code content
 * @throws {Error} If fetch fails
 * 
 * @example
 * const codeData = await ziroChatInterfaces.getCode('56dCAJWFE9vFbIs');
 * const code = codeData.code;
 */
export const getCode = async (projectCode) => {
  if (!projectCode) {
    throw new Error('Project code is required');
  }
  
  const backendUrl = getBackendUrl();
  // Get session_id from cookie or localStorage (optional - fetch_code is public but we include it for logging)
  let sessionId = getCookie('ziroatoken');
  if (!sessionId) {
    sessionId = localStorage.getItem('ziro_session_id');
  }
  // Verify it's a session_id (UUID format), not legacy JWT
  const isSessionId = sessionId && sessionId.includes('-') && sessionId.length === 36 && !sessionId.includes('.');
  
  // Note: fetch_code doesn't require auth, but we include session_id if available for logging
  const apiEndpoint = `${backendUrl}/fetch_code?code=${projectCode}${isSessionId ? `&session_id=${sessionId}` : ''}`;
  
  console.log('📝 Fetching code:', apiEndpoint.replace(sessionId || '', '***'));
  
  try {
    const response = await fetch(apiEndpoint, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    
    const data = await response.json();
    
    // Production returns { code: "..." } format
    return {
      success: true,
      code: data.code || data.content || '',
      data: data
    };
  } catch (error) {
    console.error('❌ Error fetching code:', error);
    return {
      success: false,
      error: error.message,
      code: ''
    };
  }
};

/**
 * Save project code to backend using production save function
 * Handles authentication, error messages, and success notifications
 * 
 * @param {string} code - Complete code content to save
 * @returns {Promise<void>}
 * @throws {Error} If production function is not available
 * 
 * @example
 * const code = editor.getValue();
 * await ziroChatInterfaces.saveCodeToBackend(code);
 */
export const saveCodeToBackend = async (code) => {
  if (typeof window.saveCodeToBackend === 'function') {
    return await window.saveCodeToBackend(code);
  }
  throw new Error('saveCodeToBackend function not available');
};

export default {
  isChatJSLoaded,
  sendMessage,
  initializeThread,
  getThreadId,
  getLastResponse,
  storeThreadId,
  storeResponseFeedback,
  fetchProfileAndCheckAuthorization,
  isUserAuthorized,
  getLoggedInUserInfo,
  getProject,
  getCode,
  saveCodeToBackend
};
